import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useVenti } from 'venti'
import {
	EnabledServices,
	SiteConfiguration,
} from '@matech/thebigpos-sdk'
import { useQuery } from '@tanstack/react-query'
import { generatePath, useNavigate } from 'react-router-dom'
import { CardItem } from '../CardItem'
import { Loading } from '../Loading'
import useUser from '../../hooks/useUser'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import { getTheme } from '../../config'
import { useAppContext } from '../AppContext'
import {
	getActionsByRole,
	navigationLinking,
} from '../../services/navigation'
import { MIDDLE_SCREEN } from '../../pages/protected/portal/constants'
import { disclosuresSSOSignIn } from '../../services/utils'
import { eventTypes } from '../../services/constants'
import { useMixpanel } from '../../hooks/useMixpanel'
import { useAlert } from '../../hooks'
import ModalAddCoBorrower from '../modals/ModalAddCoBorrower'
import { getErrorMessage } from '../../services/helper'
import { useLoanContext } from '../../contexts/LoanContext'
import { Action } from '../../types'
import en from '../../i18n/en'
import es from '../../i18n/es'

export type NavigationLinkingMap = typeof navigationLinking

const theme = getTheme()

const localeMap = {
	en,
	es,
}

const PortalActionCards = () => {
	const { user, canUseTasks } = useUser()
	const ventiState = useVenti()
	const mixpanel = useMixpanel()
	const navigate = useNavigate()
	const { state } = useAppContext()
	const { alert } = useAlert()
	const {
		loanId,
		loan,
		loanData: legacyLoan,
		isLoading,
		isRefetching,
	} = useLoanContext()

	const [addCoBorrowerVisible, setAddCoBorrowerVisible] =
		useState(false)
	const [currentLocale, setCurrentLocale] = useState<'en' | 'es'>(
		'en'
	)

	const { siteConfig, authToken } = state as {
		siteConfig: SiteConfiguration | null
		authToken: string | null
	}

	const coBorrowerRoute = ventiState.get(
		theme.storageKeys.coBorrowerRoute
	)

	const { data: loanTasks, error: loanTasksError } = useQuery({
		queryKey: ['loanTasks', loanId],
		queryFn: () => TheBigPOSApi.getLoanTasks(loanId),
		enabled: !!loanId && canUseTasks,
		refetchOnWindowFocus: false,
	})

	const getPageActions = (actions: Action[]) => {
		const enabledActions = actions.filter(
			(action) =>
				siteConfig?.enabledServices[
					action.id as keyof EnabledServices
				] !== false
		)

		if (window.innerWidth < MIDDLE_SCREEN) {
			return enabledActions.filter((item) => item.id !== 'Workflows')
		}

		return enabledActions
	}

	const activeTasks = useMemo(() => {
		return loanTasks?.data.filter(
			(task) => task.status === 'Outstanding'
		)
	}, [loanTasks])

	const outstandingTaskCount = activeTasks?.length ?? 0

	const actions = useMemo(() => {
		if (!siteConfig) {
			return []
		}

		return getPageActions(
			getActionsByRole(user, siteConfig, {
				loanId,
				locked: legacyLoan?.LoanLocked === 'True',
			})
		)
		// ignore ventiState dependency to avoid infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [legacyLoan?.LoanLocked, loanId, siteConfig, user])

	const openCoBorrowerModal = useCallback(() => {
		if (legacyLoan?.LoanLocked !== 'True') {
			setAddCoBorrowerVisible(true)
		} else {
			alert(
				localeMap[currentLocale].add_coborrower_form
					.loan_locked_notice,
				{
					severity: 'error',
				}
			)
		}
	}, [alert, currentLocale, legacyLoan?.LoanLocked])

	const handleAddCoBorrower = useCallback(() => {
		setCurrentLocale('en')
		openCoBorrowerModal()
	}, [openCoBorrowerModal])

	const handleAddCoBorrowerEsp = () => {
		setCurrentLocale('es')
		openCoBorrowerModal()
	}

	const handleDuplicateLoan = () => {
		navigate(`/${navigationLinking.Apply}`)
	}

	const handleDisclosures = () => {
		disclosuresSSOSignIn(siteConfig, authToken)
	}

	const handleOpenLOConnect = () => {
		let url = 'https://encompassloconnect.com/pipeline'

		if (loanId) {
			const formId = '3ea1f6a1-afc2-4b01-b299-e69e9ba6262e'
			url += `/${loanId.slice(1, -1)}/standard-forms/${formId}`
		}
		window.open(url, '_blank', 'noopener,noreferrer')
	}

	const handleOpenLoanDetails = () => {
		const navigatePath = generatePath(
			`/${navigationLinking.LoanApplication}`,
			{ loanId }
		)
		navigate(navigatePath)
	}

	const handleAppLink = (action: Action) => {
		if (action?.id === 'support') {
			mixpanel.trackEvent(eventTypes.NEED_HELP)
		} else if (action?.id === 'encompassWeb') {
			mixpanel.trackEvent(eventTypes.ENCOMPASS_WEB)
		}

		if (action.route) navigate(`/${action.route}`, { state: action })
		else if (action.action) {
			switch (action.action) {
				case 'invite':
					handleAddCoBorrower()
					break
				case 'inviteEsp':
					handleAddCoBorrowerEsp()
					break
				case 'duplicate':
					handleDuplicateLoan()
					break
				case 'disclosures':
					handleDisclosures()
					break
				case 'loConnect':
					handleOpenLOConnect()
					break
				case 'loanDetails':
					handleOpenLoanDetails()
					break
				default:
			}
		} else if (action.href) {
			window.open(action.href, '_blank', 'noopener,noreferrer')
		}
	}

	const coBorrowerBlacklist: string[] = useMemo(() => {
		if (!loan?.userLoans) {
			return []
		}
		return loan.userLoans
			.filter((user) => user.role === 'Borrower')
			.map((user) => user.user?.email ?? 'undefined')
	}, [loan?.userLoans])

	useEffect(() => {
		if (coBorrowerRoute) {
			ventiState.unset(theme.storageKeys.coBorrowerRoute)
			handleAddCoBorrower()
		}
		// ignore ventiState dependency to avoid infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [coBorrowerRoute, handleAddCoBorrower])

	useEffect(() => {
		if (loanTasksError) {
			alert(getErrorMessage(loanTasksError), { severity: 'error' })
			ventiState.set(
				theme.storageKeys.errorMessage,
				getErrorMessage(loanTasksError) ??
					theme.api_messages.server_error
			)
		}
		// ignore ventiState dependency to avoid infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [alert, loanTasksError])

	return (
		<>
			<div
				className="flex flex-row flex-wrap justify-center max-w-screen-xl mx-auto mb-10"
				style={{ minHeight: '300px' }}
			>
				{isLoading || isRefetching ? (
					<Loading size="small" />
				) : (
					actions.map((action, index) => {
						const handleClick = () => {
							handleAppLink(action)
						}

						return (
							<CardItem
								key={`action__${action.id}__${action.name}`}
								item={action}
								index={index}
								onClick={handleClick}
								source="Portal"
								notificationCount={
									action.id === 'tasks' ? outstandingTaskCount : 0
								}
							/>
						)
					})
				)}
			</div>
			<ModalAddCoBorrower
				blacklist={coBorrowerBlacklist}
				role={user?.role}
				visible={addCoBorrowerVisible}
				setVisible={setAddCoBorrowerVisible}
				siteConfig={siteConfig}
				locale={currentLocale}
			/>
		</>
	)
}

export default PortalActionCards
